import { LinearProgress, makeStyles } from '@material-ui/core';
import axios from 'axios';
import { pick } from 'lodash';
import { memo, useEffect } from 'react';
import TagManager from 'react-gtm-module';
import { useTranslation } from 'react-i18next';
import NotFoundPage from 'src/pages/404';

import CarDetailContent from '../components/CarDetailContent';
import { useCarDetail } from '../hooks/useCarDetail';
import { useExtendUsersnap } from '../hooks/useExtendUsersnap';
import { useValuationSettings } from '../hooks/useValuationSettings';
import { DEFAULT_VALUATION_COUNTRY } from '../modules/data';
import { SourceType, ValuationCountryCode } from '../modules/generated/api';
import { getCarDetailsTM, getPageViewTM, mergeDL } from '../modules/tag-manager-helpers';

const useStyles = makeStyles({
  loadingBar: {
    width: '100%',
    marginTop: '8px',
  },
});

type CarsDetailProps = {
  source: SourceType;
  carId: string;
  isDetailPage?: boolean;
  onRefresh?: () => Promise<unknown>;
  valuationCountry?: ValuationCountryCode;
};

const CarDetail = ({ source, carId, isDetailPage, onRefresh, valuationCountry }: CarsDetailProps) => {
  const classes = useStyles();
  const { valuationCountry: globalValuationCountry } = useValuationSettings();
  const finalValuationCountry = valuationCountry || globalValuationCountry || DEFAULT_VALUATION_COUNTRY;
  const { data: carDetail, error, isLoading, refetch } = useCarDetail(carId, source, finalValuationCountry);
  const {
    i18n: { language },
  } = useTranslation();

  useEffect(() => {
    if (isDetailPage && carDetail) {
      document.title = `ALLUC - ${carDetail.vin}`;
      TagManager.dataLayer(mergeDL(getCarDetailsTM(carDetail), getPageViewTM(language, 'Car details')));
    }
  }, [isDetailPage, carDetail, language]);

  useExtendUsersnap(
    {
      carDetail: pick(carDetail, 'carId', 'source', 'vin'),
    },
    [carDetail],
  );

  return (
    <>
      {axios.isAxiosError(error) && (error?.response?.status === 400 || error?.response?.status === 404) && (
        <NotFoundPage />
      )}
      {isLoading && <LinearProgress className={classes.loadingBar} />}
      {carDetail && (
        <CarDetailContent
          car={carDetail}
          refresh={async () => {
            await refetch();

            if (onRefresh) {
              await onRefresh();
            }
          }}
        />
      )}
    </>
  );
};

export default memo(CarDetail);
